import { motion } from "framer-motion";

/**
 * Editorial auth layout — typography does all the work.
 * Left: a fixed-viewport ink panel that stays put while the right form scrolls.
 * Right: the form, aligned on a generous canvas.
 */
export default function AuthLayout({ children, eyebrow, title, subtitle }) {
  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] bg-nexa-paper items-start">
      {/* LEFT — sticky ink editorial panel (locked to viewport height) */}
      <div className="hidden lg:flex sticky top-0 h-screen relative overflow-hidden ai-nightsurface flex-col px-16 pt-8 pb-14 xl:px-20 xl:pt-10 xl:pb-16">
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-6 rounded-full bg-[#D4A017] shadow-[0_0_10px_rgba(212,160,23,0.45)]" />
          <div className="font-semibold text-[28px] leading-none tracking-tight text-[#D4A017]">VELQUE</div>
        </div>

        <div className="max-wPrinciple 01-[560px] mt-4 xl:mt-6">
          <div className="font-semibold text-[10px] tracking-[0.28em] uppercase text-white/50 mb-6">The executive behind better business days</div>
          <h1 className=" text-[52px] xl:text-[68px] leading-[0.95] tracking-[-0.02em] text-white">
            Executive AI
            <br /> 
            for businesses
            <br />
            <em className="not-italic underline decoration-white/25 underline-offset-[10px]">built to scale.</em>
          </h1>
          <p className="mt-8 text-[15px] leading-[1.7] text-white/65 max-w-[48ch]">
           Built for founders and business owners who want to scale without adding operational complexity. <br />
           VELQUE observes your operations, prepares priorities, and helps every business day begin with clarity, confidence, and momentum.
          </p>
        </div>

        <div className="mt-auto pt-11 flex items-end justify-between gap-8">
          <div className="max-w-[32ch] text-[13px] leading-[1.7] text-white/55">
            <div className="lux-eyebrow text-white/40 mb-2">Principle 01</div>
             &quot;Preparation creates better decisions.
              Executive AI works before you do.&quot;
          </div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-white/40">© MMXXVI VELQUE</div>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex items-start justify-center p-12 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          className="w-full max-w-[560px]"
        >
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <div className="w-1 h-5 bg-nexa-accent" />
            <div className="font-semibold text-[20px] leading-none">Velque<span className="not-italic font-sans font-medium tracking-[0.14em] text-nexa-mute text-[12px] ml-1"></span></div>
          </div>

          {eyebrow && <div className="lux-eyebrow mb-1">{eyebrow}</div>}
          {title && <h1 className="text-[24px] font-semibold tracking-tight text-nexa-ink">{title}</h1>}
          {subtitle && <p className="mt-3 lux-body max-w-[48ch]">{subtitle}</p>}
          <div className={title || eyebrow ? "mt-5" : ""}>{children}</div>
        </motion.div>
      </div>
    </div>
  );
}
